import { FullGestureState, createUseGesture, dragAction, pinchAction } from '@use-gesture/react';
import {
  FunctionComponent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isRefDefined } from '@/utils/isRefDefined';
import { clamp } from '@/utils/math/clamp';

const useGesture = createUseGesture([dragAction, pinchAction]);

export type Origin = [number, number];
export type PinchHandler = (
  scale: number,
  rotation: number,
  state: FullGestureState<'pinch'>
) => void;
type SetScaleAndOrigin = (scale: number, origin: Origin, maxScale?: number) => void;

interface PinchSubscriber {
  add: (handler: PinchHandler) => void;
  remove: (handler: PinchHandler) => void;
}

const noop = () => {};
const defaultPinchSubscriber =
  process.env.NODE_ENV === 'development'
    ? {
        add: () => {
          throw new Error('Missing PinchProvider');
        },
        remove: () => {
          throw new Error('Missing PinchProvider');
        },
      }
    : {
        add: noop,
        remove: noop,
      };

const PinchContext = createContext<PinchSubscriber>(defaultPinchSubscriber);

export const usePinchProvider = (preventDrag: boolean) => {
  const container = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [maxScale, setMaxScale] = useState(4);
  const [origin, setOrigin] = useState<Origin>([0.5, 0.5]);
  const startPinch = useRef<{ scale: number; origin: Origin; size: [number, number] }>();

  // prevent drag to happen at the end of a pinch
  const canDrag = useRef(false);

  useGesture(
    {
      onDrag: ({ first, last, dragging, pinching, xy, initial, cancel }) => {
        if (pinching) {
          return cancel();
        }

        const containerElement = container.current;
        if (preventDrag || scale <= 1 || !containerElement) {
          return;
        }

        if (first) {
          startPinch.current = {
            scale,
            origin,
            size: [containerElement.clientWidth, containerElement.clientHeight],
          };
          canDrag.current = true;
        }

        if (dragging && canDrag.current && isRefDefined(startPinch)) {
          const {
            origin: [originX, originY],
            size: [width, height],
          } = startPinch.current;
          const newX = originX + (initial[0] - xy[0]) / width / (scale - 1);
          const newY = originY + (initial[1] - xy[1]) / height / (scale - 1);
          setOrigin([clamp(newX), clamp(newY)]);
        }

        if (last) {
          startPinch.current = undefined;
        }
      },
      onPinch: (state) => {
        const { first, last, da } = state;
        if (first) {
          pinchData.current = da;
        }

        if (isRefDefined(pinchData)) {
          const [d, a] = da;
          const [initialD, initialA] = pinchData.current;

          const scale = d / initialD;
          const rotation = a - initialA;

          const handlers =
            pinchHandlers.current.size === 0 ? [defaultHandler] : Array.from(pinchHandlers.current);
          handlers.forEach((handler: PinchHandler) => handler(scale, rotation, state));
        }

        if (last) {
          pinchData.current = undefined;
        }
      },
    },
    { target: container }
  );

  const defaultHandler: PinchHandler = (pinchScale, rotationChange, pinchState) => {
    const containerElement = container.current;
    const { first, last, pinching } = pinchState;
    if (first && containerElement) {
      startPinch.current = {
        scale,
        origin,
        size: [containerElement.clientWidth, containerElement.clientHeight],
      };
    }

    if (pinching && isRefDefined(startPinch)) {
      canDrag.current = false;
      const {
        scale,
        origin: [originX, originY],
        size: [width, height],
      } = startPinch.current;
      // clamp the scale between 1 and 4
      const newScale = clamp(scale * pinchScale, maxScale, 1);
      setScale(newScale);
      const [x, y] = pinchState.origin;
      // scaleDiff prevents jumping between multiple pinches by smoothing between the old and new pinch origin
      const scaleDiff = Math.min(Math.abs(newScale - scale), 1);

      const diffX = (x / width - 0.5) / newScale;
      const diffY = (y / height - 0.5) / newScale;

      setOrigin([clamp(originX + diffX * scaleDiff), clamp(originY + diffY * scaleDiff)]);
    }

    if (last && scale === 1) {
      setOrigin([0.5, 0.5]);
      startPinch.current = undefined;
    }
  };

  const pinchHandlers = useRef<Set<PinchHandler>>(new Set<PinchHandler>());
  const pinchData = useRef<[number, number]>();

  const pinchSubscriber = useMemo<PinchSubscriber>(
    () => ({
      add: (handler) => pinchHandlers.current.add(handler),
      remove: (handler) => pinchHandlers.current.delete(handler),
    }),
    []
  );

  const PinchProvider = useCallback<FunctionComponent<{ children: ReactNode }>>(
    ({ children }) => {
      return <PinchContext.Provider value={pinchSubscriber}>{children}</PinchContext.Provider>;
    },
    [pinchSubscriber]
  );

  const setScaleAndOrigin = useCallback<SetScaleAndOrigin>(
    (scale, origin, maxScale) => {
      setScale(scale);
      setOrigin(origin);

      if (maxScale) {
        setMaxScale(maxScale);
      }
    },
    [setScale, setOrigin]
  );

  return [container, scale, origin, PinchProvider, setScaleAndOrigin] as const;
};

export const usePinchSubscriber = () => useContext(PinchContext);
